import { createApi } from "@reduxjs/toolkit/query/react";
import { mutations, queries } from "./apiDetails";
import { transformErrorResponse } from "./handleErrors";
import customFetchBase from "./baseQuery";
import { tags } from "./constants";


export const apiSlice = createApi({
  reducerPath: "apiSlice",
  baseQuery: customFetchBase,
  tagTypes: Object.keys(tags),
  endpoints: (build) => {
    const endPoints = {};

    Object.keys(mutations).forEach((key) => {
      const apiDetails = mutations[key];
      endPoints[key] = build.mutation({
        query: ({params , body}) => {
          const headers = apiDetails?.getHeaders?.(params);
          return {
            url: apiDetails.getUrl(params),
            method: apiDetails.method,
            body,
            ...(headers && Object.keys(headers).length ? { headers } : {}),
          };
        },
        invalidatesTags: apiDetails.invalidatesTags,
        transformResponse: (response, meta, arg) =>
          apiDetails.transformResponse(response),
        transformErrorResponse: (response, meta, arg) =>
          apiDetails.transformErrorResponse
            ? apiDetails.transformErrorResponse(response)
            : transformErrorResponse(response),
      });
    });

    Object.keys(queries).forEach((key) => {
      const apiDetails = queries[key];
      endPoints[key] = build.query({
        query: (params) => {
          const headers = apiDetails?.getHeaders?.(params);
          return {
            url: apiDetails?.getUrl(params),
            method: apiDetails?.method,
            ...(headers && Object.keys(headers).length ? { headers } : {}),
          };
        },
        providesTags: apiDetails.providesTags,
        transformResponse: (response, meta, arg) =>
          apiDetails.transformResponse(response),
        transformErrorResponse: (response, meta, arg) =>
          transformErrorResponse(response),
      });
    });

    return endPoints;
  },
});

export const {
  useDomoQuery,
  useMyJourneysQuery,
  useGenerateCampaignTemplateReportMutation,
  useUpdateCampaignTemplateStatusMutation,
  useMarkAsDefaultCampaignMutation,
  useAddSuperAdminTouchpointMutation,
  useGetSendersQuery,
  useGetTouchPointsQuery,
  useGetTouchPointByIdMutation,
  useAddJourneyMutation,
  useGetJourneyTemplatesQuery,
  useGetCampaignTemplateByIdQuery,
  useGetJourneyCategoryQuery,
  useUpdateSuperAdminTouchpointMutation,
  useGetTouchPointsListQuery,
  useEditJourneyMutation,
  useUpdateSuperAdminTouchpointStatusMutation,
  useGetSuperAdminJourneyCategoryQuery,
  useAddSuperAdminJourneyCategoryMutation,
  useUpdateSuperAdminJourneyCategoryMutation,
  useGetMeetingLocationsQuery,
  useGetJourneyCategoryByIdMutation,
  useGetAdminJourneyTemplatesQuery,
  useGetCandidateConfidenceScoreQuery,
  useGetCandidateConvertedStatusQuery,
  useGetCandidatesAverageEngagementScoreQuery,
  useGetCampaignTemplateReportsQuery,
  useGetTouchPointInstanceEffectivenessQuery,
  useGetTopCampaignTemplatesQuery,
  useGetCandidatesQuery,
  useGetHiringManagerPortalCandidatesQuery,
  useLazyGetHiringManagerPortalCandidatesQuery,
  useGetCompaniesByUserIdQuery,
  useGetRolesQuery,
  useGetCompaniesQuery,
  useGetBasicCompanyDetailsQuery,
  useAddCompanyMutation,
  useGetAllUsersQuery,
  useValidateCandidatesFromCSVMutation,
  useAddCandidatesFromCSVMutation,
  useGetPositionsQuery,
  useGetCompanyQuery,
  useGetUsersQuery,
  useAddRecruiterMappingMutation,
  useDeleteRecruiterMappingMutation,
  useEditRecruiterMappingMutation,
  useGetMappedRecruitersQuery,
  useGetUserFiltersQuery,
  useGetDesignationsQuery,
  useUpdateCompanySettingsMutation,
  useUpdateCompanyFontsMutation,
  useUpdateCompanyThemesMutation,
  useUpdatePositionJourneyMutation,
  useCreateCallHistoryMutation,
  useUpdateCallHistoryMutation,
  useGetCallHistoryQuery,
  useLazyGetCallHistoryQuery,
  useGetEmployeesQuery,
  useValidateEmployeesFromCSVMutation,
  useAddEmployeesFromCSVMutation,
  useLazyGetEmployeesReportQuery,
  useDeactivateEmployeeMutation,
  useGetEmployeeByIDQuery,
  useUpdateWorkEmailMutation,
  useGetPublishedNewsFeedQuery,
  useGetLinkedInPostsQuery,
  useUpdateTimeSlotStatusMutation,
  useSendExpiredSlotsSupportEmailMutation,
  useGetTimeSlotsQuery,
  useGetConflictingTimeSlotsQuery,
  useGetHiringManagerConflictingTimeSlotsQuery,
  useAddEngagementActivityMutation,
  useRetriggerMeetingSlotsLinkMutation,
  useUpdateUserFromAdminMutation,
  useAddUserFromAdminMutation,
  useAuthEmployeeMagicLinkMutation,
  useGetCandidateTouchPointInstancesQuery,
  useGetCandidateTaskErrorsQuery,
  useRunTouchPointInstanceMutation,
  useSubmitHiringManagerMeetingPersonFeedbackMutation,
  useGetHiringManagerFeedbackInfoQuery,
  useLazyGetPositionsQuery,
  useUpdateTouchPointInstanceSendReminderMutation,
  useGetUsersForSpocQuery,
  useCreateMeetingLocationMutation,
  useDeleteMeetingLocationMutation,
  useUpdateMeetingLocationMutation,
  useGetUsersForFilterQuery,
  useGetJourneysForFilterQuery,
  useGetCompaniesForFilterQuery,
  useGetCompanyAdminsForFilterQuery,
  useGetRolesForFilterQuery,
  useGetCompanyCustomAttributesForFilterQuery,
  useDomoEmbedTokenQuery,
  useGetDomoReportsQuery,
  useGetDomoReportByIdQuery,
  useCreateDomoReportMutation,
  useUpdateDomoReportMutation,
  useDeleteDomoReportMutation,
  useGetHiringManagerPortalFiltersQuery,
  useValidateHiringManagerMagicLinkQuery,
  useResendHiringManagerMagicLinkMutation,
  useCreateHiringManagerTimeSlotsMutation,
} = apiSlice;
